import {
  type RestrictivePractice,
  type RestrictivePracticeInterventionTypeEnum,
  type RestrictivePracticeMethodEnum,
  type RestrictivePracticeOutcomeEnum,
  RestrictivePracticeTypeEnum
} from '@lmc/validations'
import {
  Form as AntdForm,
  Checkbox,
  type GetProps,
  Input,
  Radio,
  type RadioGroupProps,
  Select,
  TimePicker
} from 'antd'
import dayjs, { type Dayjs } from 'dayjs'
import { NAMESPACE } from 'locale'
import { incidentReportsConstants } from 'locale/incidentReports'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import StepDrawer from '../../StepDrawer'
import StepField, { useStepFieldLabelId } from '../../StepField'
import StepYesNo from '../../StepYesNo'
import { stepChoiceColumn, TIME_FORMAT } from '../../stepLayout'
import { keyOptions } from '../../stepOptions'
import { trimmed, withoutBlanks } from '../../stepValues'
import { toPracticeTimes } from './practiceTimes'
import {
  INTERVENTION_TYPE_KEYS,
  METHOD_KEYS,
  OUTCOME_KEYS,
  PRACTICE_TYPE_KEYS
} from './restrictivePracticeOptions'

type PracticeFormValues = {
  deEscalationAttempted?: boolean
  interventionType?: RestrictivePracticeInterventionTypeEnum
  method?: RestrictivePracticeMethodEnum
  notes?: string
  outcomes?: Array<RestrictivePracticeOutcomeEnum>
  reasonForUse?: string
  times?: [Dayjs | null, Dayjs | null] | null
  type?: RestrictivePracticeTypeEnum
}

type RestrictivePracticeDrawerProps = {
  /** The practice being edited; absent when adding one. */
  practice?: RestrictivePractice
  /** When the incident started: the practice's times fall after it. */
  incidentStart: Dayjs
  onCancel: () => void
  onSave: (practice: RestrictivePractice) => void
  testId: string
}

const toFormValues = (practice?: RestrictivePractice): PracticeFormValues => ({
  deEscalationAttempted: practice?.deEscalationAttempted,
  interventionType: practice?.interventionType,
  method: practice?.method,
  notes: practice?.notes,
  outcomes: practice?.outcomes ?? [],
  reasonForUse: practice?.reasonForUse,
  times: practice?.startedAt
    ? [
        dayjs(practice.startedAt),
        practice.endedAt ? dayjs(practice.endedAt) : null
      ]
    : null,
  type: practice?.type
})

/** Adds or edits one restrictive practice; the step saves it with the rest. */
const RestrictivePracticeDrawer = ({
  practice,
  incidentStart,
  onCancel,
  onSave,
  testId
}: RestrictivePracticeDrawerProps) => {
  const { t } = useTranslation(NAMESPACE.INCIDENT_REPORTS)
  const [form] = AntdForm.useForm<PracticeFormValues>()

  const toPractice = (
    values: PracticeFormValues,
    type: RestrictivePracticeTypeEnum
  ): RestrictivePractice => {
    const [start, end] = values.times ?? [null, null]

    return {
      type,
      ...(values.method ? { method: values.method } : {}),
      ...toPracticeTimes(incidentStart, start, end),
      ...withoutBlanks({
        deEscalationAttempted: values.deEscalationAttempted,
        interventionType: values.interventionType,
        notes: trimmed(values.notes),
        outcomes: values.outcomes,
        reasonForUse: trimmed(values.reasonForUse)
      })
    }
  }

  return (
    <StepDrawer
      form={form}
      initialValues={toFormValues(practice)}
      onValuesChange={(changed) => {
        // Only a physical intervention has a method.
        if (
          'type' in changed &&
          changed.type !== RestrictivePracticeTypeEnum.physicalIntervention
        ) {
          form.setFieldValue('method', undefined)
        }
      }}
      onCancel={onCancel}
      onConfirm={(values) => {
        if (values.type) {
          onSave(toPractice(values, values.type))
        }
      }}
      title={t(
        practice
          ? incidentReportsConstants.CASE_V2_RESTRICTIVE_EDITING
          : incidentReportsConstants.CASE_V2_RESTRICTIVE_ADDING
      )}
      confirmLabel={t(incidentReportsConstants.CASE_V2_SAVE)}
      testId={testId}
    >
      <StepField
        name="type"
        isRequired
        label={t(incidentReportsConstants.CASE_V2_RESTRICTIVE_TYPE)}
      >
        <Select
          data-testid={`${testId}-type`}
          options={keyOptions(PRACTICE_TYPE_KEYS, t)}
          placeholder={t(incidentReportsConstants.CASE_V2_SELECT_PLACEHOLDER)}
        />
      </StepField>
      <AntdForm.Item noStyle dependencies={['type']}>
        {({ getFieldValue }) =>
          getFieldValue('type') ===
            RestrictivePracticeTypeEnum.physicalIntervention && (
            <StepField
              name="method"
              label={t(incidentReportsConstants.CASE_V2_RESTRICTIVE_METHOD)}
            >
              <Select
                data-testid={`${testId}-method`}
                options={keyOptions(METHOD_KEYS, t)}
                placeholder={t(
                  incidentReportsConstants.CASE_V2_SELECT_PLACEHOLDER
                )}
              />
            </StepField>
          )
        }
      </AntdForm.Item>
      {/* A plain range picker: an end before the start is the next day. */}
      <StepField
        name="times"
        label={t(incidentReportsConstants.CASE_V2_RESTRICTIVE_DURATION)}
      >
        <TimePicker.RangePicker
          data-testid={`${testId}-times`}
          format={TIME_FORMAT}
          order={false}
          allowEmpty={[false, true]}
          placeholder={[
            t(incidentReportsConstants.CASE_V2_START_TIME),
            t(incidentReportsConstants.CASE_V2_END_TIME)
          ]}
        />
      </StepField>
      <StepField
        name="reasonForUse"
        label={t(incidentReportsConstants.CASE_V2_RESTRICTIVE_REASON)}
        hint={t(incidentReportsConstants.CASE_V2_RESTRICTIVE_REASON_HINT)}
      >
        <Input.TextArea
          data-testid={`${testId}-reason`}
          autoSize={{ minRows: 3 }}
        />
      </StepField>
      <StepField
        name="deEscalationAttempted"
        label={t(incidentReportsConstants.CASE_V2_RESTRICTIVE_DE_ESCALATION)}
      >
        <StepYesNo data-testid={`${testId}-de-escalation`} />
      </StepField>
      <StepField
        name="interventionType"
        label={t(
          incidentReportsConstants.CASE_V2_RESTRICTIVE_INTERVENTION_TYPE
        )}
      >
        <NamedRadios
          data-testid={`${testId}-intervention-type`}
          options={keyOptions(INTERVENTION_TYPE_KEYS, t)}
        />
      </StepField>
      <StepField
        name="outcomes"
        label={t(incidentReportsConstants.CASE_V2_RESTRICTIVE_OUTCOME)}
        hint={t(incidentReportsConstants.CASE_V2_RESTRICTIVE_OUTCOME_HINT)}
      >
        <NamedCheckboxes
          data-testid={`${testId}-outcomes`}
          options={keyOptions(OUTCOME_KEYS, t)}
        />
      </StepField>
      <StepField
        name="notes"
        label={t(incidentReportsConstants.CASE_V2_RESTRICTIVE_NOTES)}
        hint={t(incidentReportsConstants.CASE_V2_RESTRICTIVE_NOTES_HINT)}
      >
        <Input.TextArea
          data-testid={`${testId}-notes`}
          autoSize={{ minRows: 3 }}
        />
      </StepField>
    </StepDrawer>
  )
}

/* A field's <label> can't name a group of controls, so each names itself. */
const NamedRadios = (props: RadioGroupProps) => {
  const labelId = useStepFieldLabelId()

  return (
    <div role="radiogroup" aria-labelledby={labelId}>
      <VerticalRadios {...props} />
    </div>
  )
}

const NamedCheckboxes = (props: GetProps<typeof Checkbox.Group>) => {
  const labelId = useStepFieldLabelId()

  return (
    <Fieldset aria-labelledby={labelId}>
      <VerticalCheckboxes {...props} />
    </Fieldset>
  )
}

const Fieldset = styled.fieldset`
  border: 0;
  margin: 0;
  min-width: 0;
  padding: 0;
`

const VerticalRadios = styled(Radio.Group)`
  ${stepChoiceColumn}
`

const VerticalCheckboxes = styled(Checkbox.Group)`
  ${stepChoiceColumn}
`

export default RestrictivePracticeDrawer
